import { useCallback } from 'react';

import { useBoolean } from '@dreryk/react-common';
import { Link, ModalConfirmButton } from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';

import { BlockedAccountTooltip } from '../../../../../common/BlockedAccountTooltip';
import { appointmentsTranslationKeys } from '../../../../../translations';
import { AppointmentUpdateModal } from '../../../../appointment/update/shared/AppointmentUpdateModal';
import { AppointmentWaitlistUpdateModal } from '../../../../appointment/update/waitlist/AppointmentWaitlistUpdateModal';
import { AppointmentUpdateActionEventInterceptor } from '../../../../appointment/update/interceptors/AppointmentUpdateActionEventInterceptor';

type AppointmentDetailsTermUpdateLinkProps = {
   readonly appointmentId: number;
   readonly onAppointmentUpdated: VoidFunction;
   readonly timeSlotConfirmed: boolean;
   readonly sourceLabel?: string;
};

// eslint-disable-next-line max-lines-per-function
export const AppointmentDetailsTermUpdateLink = (props: AppointmentDetailsTermUpdateLinkProps): JSX.Element => {
   const [{ setTrue: openModal, setFalse: closeModal }, modalOpened] = useBoolean(false);
   const creatingAppointmentsAvailable = protectFeature('creatingAppointments');

   const handleSuccess = useCallback((): void => {
      props.onAppointmentUpdated();
      closeModal();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [closeModal, props.onAppointmentUpdated]);

   const renderModal = (): JSX.Element | null => {
      if (!modalOpened) {
         return null;
      }

      if (!props.timeSlotConfirmed && props.sourceLabel === 'waitlist') {
         return (
            <AppointmentWaitlistUpdateModal
               title={appointmentsTranslationKeys.update.modal.title.update}
               appointmentId={props.appointmentId}
               onClose={closeModal}
               confirmButton={({ handleSubmit }) => (
                  <AppointmentUpdateActionEventInterceptor
                     appointmentId={props.appointmentId}
                     handleSubmit={handleSubmit}
                  >
                     <ModalConfirmButton
                        label={appointmentsTranslationKeys.update.modal.confirm.update}
                        onClick={handleSuccess}
                     />
                  </AppointmentUpdateActionEventInterceptor>
               )}
            />
         );
      }

      return (
         <AppointmentUpdateModal
            title={appointmentsTranslationKeys.update.modal.title.update}
            appointmentId={props.appointmentId}
            onClose={closeModal}
            confirmButton={({ handleSubmit }) => (
               <AppointmentUpdateActionEventInterceptor appointmentId={props.appointmentId} handleSubmit={handleSubmit}>
                  <ModalConfirmButton
                     label={appointmentsTranslationKeys.update.modal.confirm.update}
                     onClick={handleSuccess}
                  />
               </AppointmentUpdateActionEventInterceptor>
            )}
         />
      );
   };

   return (
      <>
         <BlockedAccountTooltip disabled={creatingAppointmentsAvailable} testId={'appointmentTermUpdate'}>
            <Link
               onClick={openModal}
               testId={'appointmentTermUpdate'}
               label={appointmentsTranslationKeys.update.title}
               disabled={!creatingAppointmentsAvailable}
            />
         </BlockedAccountTooltip>
         {renderModal()}
      </>
   );
};
